/*
 * File: biometric-auth.service.ts
 * Project: rast-app
 * Create Date: Thursday, 17th September 2026
 * Author: Pedro Santos (pedro.santos@vonbraunlabs.com)
 * -----
 * Last Modified: Thursday, 17th September 2026
 * Modified by: Pedro Santos (pedro.santos@vonbraunlabs.com)
 * -----
 * Copyright (c) 2026 CPA Wernher von Braun
 * ----
 * HISTORY:
 * Date      	By	Comments
 * ----------	---	---------------------------------------------------------
 */

import { Injectable } from '@angular/core';
import { AlertController, Platform } from '@ionic/angular/standalone';
import { Preferences } from '@capacitor/preferences';
import { NativeBiometric } from '@capgo/capacitor-native-biometric';
import { DeviceSecurityDetect } from '@capacitor-community/device-security-detect';
import { ToastService } from '../toast/toast.service';

const CREDENTIAL_SERVER = 'com.vonbraun.rast.biometric-login';
const ENABLED_PREFERENCE_KEY = 'biometric_login_enabled';
const PROMPT_DISMISSED_PREFERENCE_KEY = 'biometric_login_prompt_dismissed';

/**
 * Centraliza todo o acesso aos plugins nativos de biometria
 * (@capgo/capacitor-native-biometric), detecção de dispositivo comprometido
 * (@capacitor-community/device-security-detect) e à flag de preferência que
 * indica se o login por biometria está habilitado. Nenhum outro arquivo deve
 * chamar esses plugins diretamente.
 */
@Injectable({
  providedIn: 'root'
})
export class BiometricAuthService {
  private autoUnlockAttempted = false;

  constructor(
    private readonly platform: Platform,
    private readonly alertController: AlertController,
    private readonly toastService: ToastService
  ) { }

  private isNative(): boolean {
    return this.platform.is('capacitor');
  }

  async isAvailable(): Promise<boolean> {
    if (!this.isNative()) return false;

    try {
      const result = await NativeBiometric.isAvailable({ useFallback: false });
      return result.isAvailable;
    } catch {
      return false;
    }
  }

  async isDeviceSecure(): Promise<boolean> {
    if (!this.isNative()) return false;

    try {
      const { value } = await DeviceSecurityDetect.isJailBreakOrRooted();
      return !value;
    } catch {
      return false;
    }
  }

  /**
   * Dispara o prompt nativo de biometria. Checa o dispositivo comprometido
   * antes de chamar o plugin, para que nenhum chamador consiga contornar
   * essa checagem invocando authenticate() diretamente.
   */
  async authenticate(reason: string): Promise<void> {
    const isSecure = await this.isDeviceSecure();
    if (!isSecure) {
      throw new Error('Dispositivo comprometido: login por biometria bloqueado.');
    }

    await NativeBiometric.verifyIdentity({
      reason,
      title: 'Login por biometria',
      subtitle: reason,
      negativeButtonText: 'Cancelar'
    });
  }

  async isEnabled(): Promise<boolean> {
    const { value } = await Preferences.get({ key: ENABLED_PREFERENCE_KEY });
    return value === 'true';
  }

  private async setEnabled(enabled: boolean): Promise<void> {
    await Preferences.set({ key: ENABLED_PREFERENCE_KEY, value: enabled ? 'true' : 'false' });
  }

  async wasPromptDismissed(): Promise<boolean> {
    const { value } = await Preferences.get({ key: PROMPT_DISMISSED_PREFERENCE_KEY });
    return value === 'true';
  }

  async markPromptDismissed(): Promise<void> {
    await Preferences.set({ key: PROMPT_DISMISSED_PREFERENCE_KEY, value: 'true' });
  }

  /**
   * Habilita o login por biometria: valida disponibilidade e segurança do
   * dispositivo, autentica e só então guarda o refresh token no Keystore/
   * Keychain via o próprio plugin de biometria.
   */
  async enable(username: string, refreshToken: string): Promise<void> {
    const available = await this.isAvailable();
    if (!available) {
      throw new Error('Biometria indisponível neste dispositivo.');
    }

    const isSecure = await this.isDeviceSecure();
    if (!isSecure) {
      throw new Error('Dispositivo comprometido: não é possível habilitar o login por biometria.');
    }

    await this.authenticate('Autentique-se para habilitar o login por biometria');

    await NativeBiometric.setCredentials({
      username,
      password: refreshToken,
      server: CREDENTIAL_SERVER
    });

    await this.setEnabled(true);
  }

  /**
   * Desabilita o login por biometria. Não exige autenticação nem faz logout
   * — apenas remove o refresh token guardado e a flag de habilitação.
   */
  async disable(): Promise<void> {
    try {
      await NativeBiometric.deleteCredentials({ server: CREDENTIAL_SERVER });
    } catch {
      // Sem credencial salva, nada a remover.
    }

    await this.setEnabled(false);
  }

  async getStoredRefreshToken(): Promise<string | null> {
    try {
      const { isSaved } = await NativeBiometric.isCredentialsSaved({ server: CREDENTIAL_SERVER });
      if (!isSaved) return null;

      const credentials = await NativeBiometric.getCredentials({ server: CREDENTIAL_SERVER });
      return credentials.password;
    } catch {
      return null;
    }
  }

  /**
   * Mantém o refresh token guardado em sincronia com a rotação feita pelo
   * backend a cada /refresh. Best-effort: nunca lança, nunca solicita
   * biometria — quem chama (interceptor ou AuthenticationService) não deve
   * ter seu fluxo alterado por isso.
   */
  async resyncRotatedRefreshToken(newRefreshToken: string): Promise<void> {
    try {
      if (!(await this.isEnabled())) return;

      const { isSaved } = await NativeBiometric.isCredentialsSaved({ server: CREDENTIAL_SERVER });
      if (!isSaved) return;

      const credentials = await NativeBiometric.getCredentials({ server: CREDENTIAL_SERVER });
      await NativeBiometric.setCredentials({
        username: credentials.username,
        password: newRefreshToken,
        server: CREDENTIAL_SERVER
      });
    } catch {
      // Sincronização é best-effort.
    }
  }

  /**
   * Garante que a tentativa automática de desbloqueio na tela de login só
   * aconteça uma vez por sessão do app (o próprio singleton do serviço já
   * reseta em todo cold start real).
   */
  consumeAutoUnlockAttempt(): boolean {
    if (this.autoUnlockAttempted) return false;

    this.autoUnlockAttempted = true;
    return true;
  }

  /**
   * Chamado após um login bem-sucedido (senha ou Google). Pergunta ao
   * usuário se deseja habilitar o login por biometria, mas só uma vez:
   * pula silenciosamente se já habilitado, já recusado antes, biometria
   * indisponível ou dispositivo comprometido.
   */
  async promptToEnableIfEligible(username: string): Promise<void> {
    if (!this.isNative()) return;
    if (await this.isEnabled()) return;
    if (await this.wasPromptDismissed()) return;
    if (!(await this.isAvailable())) return;
    if (!(await this.isDeviceSecure())) return;

    const alert = await this.alertController.create({
      header: 'Login por biometria',
      message: 'Deseja habilitar o login por biometria para acessar o RAST mais rapidamente da próxima vez?',
      backdropDismiss: false,
      buttons: [
        {
          text: 'Agora não',
          role: 'cancel',
          handler: () => {
            this.markPromptDismissed();
          }
        },
        {
          text: 'Habilitar',
          handler: () => {
            this.confirmEnableFromPrompt(username);
          }
        }
      ]
    });

    await alert.present();
  }

  private async confirmEnableFromPrompt(username: string): Promise<void> {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) return;

    try {
      await this.enable(username, refreshToken);
      this.toastService.openSuccessToast('Login por biometria habilitado com sucesso!');
    } catch {
      this.toastService.openErrorToast('Não foi possível habilitar o login por biometria.');
    }
  }
}
